"use client";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplications } from "../../../redux/features/applicationSlice";
import LimitDropdown from '../../common/LimitDropdown';
import CountryCard from "./CountryCard";
import VisaCategory from "./VisaCategory";
import CurrentApplication from "./CurrentApplication";
import { useCountries } from "./staticData";
import { setApplicationData } from "../../../redux/features/newApplicationSlice";

const SELECTED_COUNTRY_STORAGE_KEY = "selectedClientCountry";

export default function MainDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const countries = useCountries();
  // Get applications from Redux store
  const { applications, isLoading, totalPages, currentPage } = useSelector(
    (state) => state.application,
  );
  const applicationsList = Array.isArray(applications)
    ? applications
    : Array.isArray(applications?.applications)
      ? applications.applications
      : [];

  const getInitialSelectedCountry = () => {
    try {
      const storedCountry = localStorage.getItem(SELECTED_COUNTRY_STORAGE_KEY);
      if (!storedCountry) return null;
      const parsedCountry = JSON.parse(storedCountry);
      if (parsedCountry?._id && parsedCountry?.name) {
        return parsedCountry;
      }
    } catch (error) {
      console.error("Failed to parse selected country from storage:", error);
    }
    return null;
  };

  const [step, setStep] = useState(0);
  const [user, setUser] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(
    () => getInitialSelectedCountry() || {
      name: "Australia",
      flag: "https://flagcdn.com/w320/au.png",
    },
  );
  const [visaCategories, setVisaCategories] = useState([]);
  const [visaFetchAttempted, setVisaFetchAttempted] = useState(false);
  const [isVisaCategoriesLoading, setIsVisaCategoriesLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const countryDropdownRef = useRef(null);
  const [applicationPage, setApplicationPage] = useState(1);
  const [dashboardLimit, setDashboardLimit] = useState(10);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData) {
      navigate("/");
      return;
    }
    setUser(JSON.parse(userData));
  }, [dispatch, navigate]);

  useEffect(() => {
    dispatch(fetchApplications({ page: applicationPage, limit: dashboardLimit }));
  }, [applicationPage, dashboardLimit, dispatch]);

  useEffect(() => {
    const hasActiveApplication = applicationsList.some(
      (app) => app.status === "Submitted" || app.status === "In-Progress"
    );

    setStep(hasActiveApplication ? 2 : 1);
  }, [applicationsList]);

  useEffect(() => {
    if (!countries?.length) return;

    setSelectedCountry((previousCountry) => {
      if (previousCountry?._id) {
        const byId = countries.find(
          (country) => country._id === previousCountry._id,
        );
        if (byId) return byId;
      }

      const byName = countries.find(
        (country) =>
          country?.name?.toLowerCase() === previousCountry?.name?.toLowerCase(),
      );
      if (byName) return byName;

      const australia = countries.find(
        (country) => country?.name?.toLowerCase() === "australia",
      );
      return australia || countries[0];
    });
  }, [countries]);

  useEffect(() => {
    if (!selectedCountry?._id) return;

    localStorage.setItem(
      SELECTED_COUNTRY_STORAGE_KEY,
      JSON.stringify({
        _id: selectedCountry._id,
        name: selectedCountry.name,
        flag: selectedCountry.flag,
      }),
    );

    getVisaCategoryByCountryId(selectedCountry._id);
  }, [selectedCountry && selectedCountry._id]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isOpen]);

  async function getVisaCategoryByCountryId(_id) {
    setIsVisaCategoriesLoading(true);
    setVisaFetchAttempted(false);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/visatype?countryId=${_id}&isActive=true`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      const data = await response.json();
      setVisaCategories(data?.body?.visaTypes || []);
    } catch (error) {
      console.error("Error fetching visa categories:", error);
      setVisaCategories([]);
    } finally {
      setVisaFetchAttempted(true);
      setIsVisaCategoriesLoading(false);
    }
  }
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setStep(2);
  };

  const handleApplicationPageChange = (nextPage) => {
    const safeTotalPages = totalPages || 1;
    const targetPage = Math.min(Math.max(nextPage, 1), safeTotalPages);
    if (targetPage !== applicationPage) {
      setApplicationPage(targetPage);
    }
  };

  const HandleNewData = (category) => {
    // console.log(selectedCountry.name)
    // console.log(category)

    dispatch(
      setApplicationData({
        countryOfInterest: selectedCountry.name,
        visaCategory: category,
        id: "new",
        status: "In-Progress",
      })
    );
    navigate("/client/applications/form/new");
  };

  if (isLoading ) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="md:p-8">
      {step === 1 && (
        <div className="flex flex-col items-center justify-center space-y-8 pt-4 md:pt-10">
          <div className="text-center space-y-2">
            <h1 className="text-xl md:text-2xl font-semibold">
              Hi {user?.firstName}! Welcome to Worldclass Consultants
            </h1>
            <p className="text-base md:text-lg text-gray-600">
              Please choose a location to get started
            </p>
          </div>
          <div className="grid grid-cols- sm:grid-cols-3 lg:grid-cols-4 gap-6 w-[250px] md:w-full">
            {countries.map((country) => (
              <CountryCard
                key={country.name}
                country={country.name}
                flag={country.flag}
                onClick={() => handleCountrySelect(country)}
              />
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4 md:space-y-8">
          <div className="flex justify-between items-center">
            <h2 className="text-lg md:text-2xl font-semibold">
              Visa Categories
            </h2>
            
              
              <div ref={countryDropdownRef} className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-1 md:space-x-2 rounded-full px-2 md:px-4 py-2 text-sm border border-b-0 border-[#87CA60]/60 bg-white drop-shadow-md"
              >
                <img
                  loading={"lazy"}
                  src={selectedCountry?.flag || "/placeholder.svg"}
                  alt={selectedCountry?.name}
                  className="h-6 w-6 md:w-7 md:h-7 rounded-full object-cover"
                />
                <span>{selectedCountry?.name}</span>
                <svg
                  className={`w-3 h-3 md:w-4 md:h-4 transition-transform ${isOpen ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-[150px] bg-white border rounded-lg shadow-lg py-1 z-10">
                  {countries.map((country) => (
                    <button
                      key={country.name}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2 ${selectedCountry?.name === country.name
                          ? "bg-gray-50"
                          : ""
                        }`}
                      onClick={() => {
                        setSelectedCountry(country);
                        setIsOpen(false);
                      }}
                    >
                      <img
                        loading={"lazy"}
                        src={country.flag || "/placeholder.svg"}
                        alt={country.name}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                      <span>{country.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="text-sm md:text-base text-gray-600 !mt-4">
            Let's get started! Select from the tab below the type of visa you
            require to proceed further
          </p>
          {isVisaCategoriesLoading ? (
            <div className="flex items-center justify-center min-h-[140px]">
              <div className="w-7 h-7 border-4 border-[#005787] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2  gap-4">
              {visaCategories.map((category) => (
                <VisaCategory
                  key={category.title}
                  category={category.title}
                  onClick={() => HandleNewData(category.title)}
                />
              ))}
            </div>
          )}
          {!isVisaCategoriesLoading && visaFetchAttempted && visaCategories.length === 0 && (
            <p className="text-sm md:text-base text-gray-600 !m-0 !p-0 text-center">
              No Visa Categories available.
            </p>
          )}
          <div>
            {applicationsList.length > 0 &&
              applicationsList.filter(
                (application) =>
                  application.status.toLowerCase() !== "finalized" &&
                  application.status.toLowerCase() !== "withdrawn"
              )?.length > 0 && (
                <>
                  <div className="mt-8 mb-4">
                    <h2 className="text-xl font-semibold mb-4">
                      Current Application(s)
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2.5">
                    {applicationsList
                      .filter(
                        (application) =>
                          application.status.toLowerCase() !== "finalized" &&
                          application.status.toLowerCase() !== "withdrawn"
                      )
                      .map((application, index) => (
                        <CurrentApplication
                          key={index}
                          application={application}
                        />
                      ))}
                  </div>
                  <div className="flex flex-col gap-4 pt-6 md:flex-row md:items-center md:justify-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        className={`px-2 md:px-3 py-2 rounded-md ${applicationPage <= 1 ? "bg-gray-200/50 text-gray-400 cursor-default" : "bg-gray-100 text-[#005787] hover:bg-gray-300"}`}
                        disabled={applicationPage <= 1}
                        onClick={() => handleApplicationPageChange(applicationPage - 1)}
                      >
                        &lt;
                      </button>

                      {Array.from({ length: totalPages || 1 }).map((_, index) => {
                        const pageNumber = index + 1;
                        if (
                          pageNumber === 1 ||
                          pageNumber === (totalPages || 1) ||
                          (pageNumber >= applicationPage - 1 && pageNumber <= applicationPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => handleApplicationPageChange(pageNumber)}
                              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-md border ${applicationPage === pageNumber
                                  ? "bg-[#005787] text-white border-[#334155]"
                                  : "bg-gray-100 text-[#005787] hover:bg-gray-300 border-gray-200"
                                }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                        if (pageNumber === applicationPage - 2 || pageNumber === applicationPage + 2) {
                          return (
                            <span key={pageNumber} className="px-2 py-1 text-gray-500">...</span>
                          );
                        }
                        return null;
                      })}

                      <button
                        className={`px-2 md:px-3 py-2 rounded-md ${totalPages && applicationPage >= totalPages ? "bg-gray-200/50 text-gray-400 cursor-default" : "bg-gray-100 text-[#005787] hover:bg-gray-300"}`}
                        disabled={totalPages && applicationPage >= totalPages}
                        onClick={() => handleApplicationPageChange(applicationPage + 1)}
                      >
                        &gt;
                      </button>
                    </div>

                    <div className="flex items-center justify-center gap-3">
                      <div className="text-sm text-gray-600">
                        Page {currentPage || applicationPage} of {totalPages || 1}
                      </div>
                      <LimitDropdown
                        value={dashboardLimit}
                        onChange={(v) => {
                          setDashboardLimit(v);
                          setApplicationPage(1);
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
              
          </div>
        </div>
      )}
    </div>

  );
}
